import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json, Tables } from "@how/database";
import type { Block } from "@how/website-core";
import type { BlockScheduleValue } from "./block-schedule";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export type PageRow = Pick<
  Tables<"content_pages">,
  | "id"
  | "slug"
  | "title"
  | "published_slug"
  | "published_at"
  | "published_version_id"
  | "draft_updated_at"
>;

export type PageStatus = "draft" | "published" | "changed";

/** Never published; live and unchanged; or live with a draft that differs from what visitors see (ADR 0031). */
export function pageStatus(page: PageRow): PageStatus {
  if (page.published_version_id === null || page.published_at === null) return "draft";
  // Compared as instants: the two strings can differ in how many fractional digits they carry.
  return Date.parse(page.draft_updated_at) > Date.parse(page.published_at)
    ? "changed"
    : "published";
}

export const PAGE_STATUS_LABEL: Readonly<Record<PageStatus, string>> = {
  draft: "Not published",
  published: "Published",
  changed: "Unpublished changes",
};

/** The address a page has, or will have once published. */
export function pagePath(slug: string): string {
  return slug === "home" ? "/" : `/${slug}`;
}

const PAGE_COLUMNS =
  "id, slug, title, published_slug, published_at, published_version_id, draft_updated_at";

/** This church's live pages. A church has tens of pages, not thousands, so the list is read whole. */
export function usePages() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["pages", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<PageRow[]> => {
      const { data, error } = await supabase
        .from("content_pages")
        .select(PAGE_COLUMNS)
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("title");
      if (error) throw error;
      return data;
    },
  });
}

/**
 * A block as the editor holds it: the contract's fields, the id it keeps once saved, and its optional show-from /
 * show-until window (ADR 0031, amendment of 2026-10-03), which is stored beside the content rather than in it.
 */
export type EditorBlock = Block & {
  readonly id?: string;
  readonly schedule?: BlockScheduleValue | null;
};

export interface PageDetail {
  readonly page: Tables<"content_pages">;
  readonly blocks: readonly EditorBlock[];
  readonly versions: readonly Pick<
    Tables<"content_page_versions">,
    "id" | "version" | "published_at" | "published_by"
  >[];
}

export function usePage(pageId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["page", tenantId, pageId],
    enabled: pageId !== null,
    queryFn: async (): Promise<PageDetail> => {
      const id = pageId ?? "";
      const [page, blocks, versions] = await Promise.all([
        supabase.from("content_pages").select("*").eq("id", id).single(),
        supabase
          .from("content_page_blocks")
          .select("id, block_type, content, show_from, show_until")
          .eq("page_id", id)
          .order("position"),
        supabase
          .from("content_page_versions")
          .select("id, version, published_at, published_by")
          .eq("page_id", id)
          .order("version", { ascending: false })
          .limit(50),
      ]);
      if (page.error) throw page.error;
      if (blocks.error) throw blocks.error;
      if (versions.error) throw versions.error;
      return {
        page: page.data,
        // Stored as a type plus a content object; the editor works with the contract's own shape. A stored block that
        // no longer matches its contract is still shown, and the editor flags it when it is opened.
        blocks: blocks.data.map(
          (row) =>
            ({
              ...(row.content as Record<string, unknown>),
              type: row.block_type,
              id: row.id,
              schedule:
                row.show_from === null && row.show_until === null
                  ? null
                  : {
                      ...(row.show_from === null ? {} : { showFrom: row.show_from }),
                      ...(row.show_until === null ? {} : { showUntil: row.show_until }),
                    },
            }) as EditorBlock,
        ),
        versions: versions.data,
      };
    },
  });
}

export type AssetRow = Pick<
  Tables<"content_assets">,
  "id" | "storage_path" | "alt_text" | "mime_type" | "width" | "height"
>;

/** Images in the church's media area, for image fields. */
export function useAssets() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["assets", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<AssetRow[]> => {
      const { data, error } = await supabase
        .from("content_assets")
        .select("id, storage_path, alt_text, mime_type, width, height")
        .eq("tenant_id", tenantId ?? "")
        .like("mime_type", "image/%")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data;
    },
  });
}

/** The church's public website host, for preview links. */
export function usePublicHost() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["public-host", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("tenant_domains")
        .select("hostname, is_primary")
        .eq("tenant_id", tenantId ?? "")
        .eq("surface", "public_web")
        .eq("status", "active")
        .order("is_primary", { ascending: false })
        .limit(1);
      if (error) throw error;
      return data[0]?.hostname ?? null;
    },
  });
}

function useWebsiteInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (pageId?: string) => {
    await client.invalidateQueries({ queryKey: ["pages", tenantId] });
    if (pageId !== undefined)
      await client.invalidateQueries({ queryKey: ["page", tenantId, pageId] });
  };
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

export function useCreatePage() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useWebsiteInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly title: string;
      readonly slug: string;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("create_page", {
        p_tenant_id: tenantId ?? "",
        p_slug: input.slug,
        p_title: input.title,
      });
      failed(error);
      return data as string;
    },
    onSuccess: async () => {
      await invalidate();
    },
  });
}

export interface PageFields {
  readonly title: string;
  readonly slug: string;
  readonly description: string;
  readonly seoTitle: string;
  readonly seoDescription: string;
  readonly socialImageId: string | null;
  readonly noindex: boolean;
}

export function useUpdatePage() {
  const supabase = useSupabase();
  const invalidate = useWebsiteInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly pageId: string; readonly fields: PageFields }) => {
      const f = input.fields;
      const { error } = await supabase.rpc("update_page", {
        p_page_id: input.pageId,
        p_slug: f.slug.trim(),
        p_title: f.title.trim(),
        p_description: f.description,
        p_seo_title: f.seoTitle,
        p_seo_description: f.seoDescription,
        ...(f.socialImageId === null ? {} : { p_social_image_id: f.socialImageId }),
        p_noindex: f.noindex,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.pageId);
    },
  });
}

export function useSaveBlocks() {
  const supabase = useSupabase();
  const invalidate = useWebsiteInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly pageId: string;
      readonly blocks: readonly EditorBlock[];
    }) => {
      const { error } = await supabase.rpc("save_page_blocks", {
        p_page_id: input.pageId,
        // Each block is plain JSON as its contract defines it, with its id when it already exists and its schedule
        // (null for always). The list's order is the page's order: a drag, an arrow or Move to top is this one call.
        p_blocks: input.blocks as unknown as Json,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.pageId);
    },
  });
}

/** One of the lifecycle calls that take only the page: publish, unpublish, delete. */
function usePageAction(
  call: (
    pageId: string,
  ) => PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>,
) {
  const invalidate = useWebsiteInvalidation();
  return useMutation({
    mutationFn: async (pageId: string): Promise<unknown> => {
      const { data, error } = await call(pageId);
      failed(error);
      return data;
    },
    onSuccess: async (_r, pageId) => {
      await invalidate(pageId);
    },
  });
}

export function usePublishPage() {
  const supabase = useSupabase();
  return usePageAction((pageId) => supabase.rpc("publish_page", { p_page_id: pageId }));
}

export function useUnpublishPage() {
  const supabase = useSupabase();
  return usePageAction((pageId) => supabase.rpc("unpublish_page", { p_page_id: pageId }));
}

export function useDeletePage() {
  const supabase = useSupabase();
  return usePageAction((pageId) => supabase.rpc("delete_page", { p_page_id: pageId }));
}

export function useRestoreVersion() {
  const supabase = useSupabase();
  const invalidate = useWebsiteInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly pageId: string; readonly versionId: string }) => {
      const { error } = await supabase.rpc("restore_page_version", {
        p_version_id: input.versionId,
      });
      failed(error);
    },
    onSuccess: async (_r, input) => {
      await invalidate(input.pageId);
    },
  });
}

/** A 30-minute token that shows this page's draft on the church's own site (ADR 0031). */
export function useCreatePreview() {
  const supabase = useSupabase();
  return useMutation({
    mutationFn: async (pageId: string): Promise<string> => {
      const { data, error } = await supabase.rpc("create_page_preview", { p_page_id: pageId });
      failed(error);
      return data as string;
    },
  });
}
